import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Manages a single URL query param that represents "which item's detail
 * modal is open" (COL-87) — e.g. Gallery's `?item=` or Entities'
 * `?enrichment=`. Both pages need the exact same push/replace/no-op rules
 * for that param; this hook is the one place that encodes them instead of
 * each page hand-rolling its own copy:
 *
 *   - `open(id)` pushes a new history entry the first time a modal opens
 *     (so browser back closes it rather than leaving the page), replaces in
 *     place when switching to a different id while a modal is already open
 *     (prev/next, a link inside search results) so that doesn't stack a
 *     back-entry per step, and is a no-op — returns `false`, nothing
 *     changes — when the id given is already the one showing, so a caller
 *     never pushes a duplicate history entry for re-opening the same item.
 *   - `close()` is always a replace: it collapses an opened-via-push entry
 *     back to "closed" in place rather than leaving a dead forward-only
 *     back-stop, and is harmless if there was nothing to pop (a deep link
 *     landed here directly).
 *
 * Deliberately does NOT own the resolved object behind that id — turning
 * `selectedId` into a full item/enrichment is cache/fetch logic specific to
 * each page, so callers keep running their own resolution effect. This hook
 * is just the URL mechanics underneath it.
 *
 * Also returns the underlying `searchParams`/`setSearchParams` (for a page
 * that needs to fold other params — category, q — into the same update as
 * closing the modal) and `setSearchParamsRef`, an always-current ref to
 * `setSearchParams`. react-router hands back a new `setSearchParams`
 * identity on every URL change (it's memoized against the current
 * `searchParams`), so an effect that depends on it directly re-runs on any
 * unrelated param edit — e.g. a resolution effect watching `selectedId`
 * re-firing just because the user typed into an unrelated search box.
 * Callers that call `setSearchParams` from inside an effect should use
 * `setSearchParamsRef.current(...)` and omit it from that effect's deps.
 */
export function useUrlModalParam(paramName) {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get(paramName) || null;

  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const open = useCallback((id) => {
    const current = searchParams.get(paramName);
    if (current === id) return false;
    setSearchParams((prev) => {
      prev.set(paramName, id);
      return prev;
    }, { replace: !!current });
    return true;
  }, [searchParams, setSearchParams, paramName]);

  const close = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete(paramName);
      return prev;
    }, { replace: true });
  }, [setSearchParams, paramName]);

  return { selectedId, open, close, searchParams, setSearchParams, setSearchParamsRef };
}
